import { useEffect, useState } from "react"
import { Card, Button, Form, Row, Col, Badge, Spinner, Table } from "react-bootstrap"
import { FaCalendarAlt, FaDollarSign, FaEdit, FaPlus, FaTrash, FaTimes } from "react-icons/fa"
import { toast } from "react-toastify"
import moment from "moment"
import { getBaseUrl } from "../../../../Redux/Config/AppConfig"
import { useHotel } from "../../../../Common/HotelContext"

const BudgetManager = () => {
  const baseUrl = getBaseUrl();
  // Use HotelContext
  const {
    selectedHotel,
  } = useHotel();
  const [budgets, setBudgets] = useState([])
  const [form, setForm] = useState({
    period_start: "",
    period_end: "",
    amount: "",
  })
  const [editingId, setEditingId] = useState(null)
  const [loading, setLoading] = useState(false)

  // Fetch budgets on mount
  useEffect(() => {
    fetchBudgets()
  }, [selectedHotel])

  const fetchBudgets = async () => {
    setLoading(true)
    try {
      if(selectedHotel){
        const token = localStorage.getItem("token");
        const res = await fetch(`${baseUrl}/budgets?hotel_id=${selectedHotel}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setBudgets(data.data || [])
      }else{
        setBudgets([])
      }
    } catch (err) {
      toast.error("Failed to fetch budgets")
    }
    setLoading(false)
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const token = localStorage.getItem("token");
      if (editingId) {
        // PUT (Update)
        await fetch(`${baseUrl}/budgets/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        });
        setEditingId(null)
        toast.success("Budget updated successfully!")
      } else {
        // POST (Create)
        await fetch(`${baseUrl}/budgets`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...form, hotel_id: selectedHotel }),
        });
        toast.success("Budget created successfully!")
      }
      setForm({ period_start: "", period_end: "", amount: "" })
      fetchBudgets()
    } catch (err) {
      toast.error("Failed to save budget")
    }
    setLoading(false)
  }

  const handleEdit = (budget) => {
    setEditingId(budget.id)
    setForm({
      period_start: budget.period_start,
      period_end: budget.period_end,
      amount: budget.amount.toString(),
    })
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this budget?")) return
    setLoading(true)
    try {
      const token = localStorage.getItem("token");
      await fetch(`${baseUrl}/budgets/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchBudgets()
      toast.success("Budget deleted successfully!")
    } catch (err) {
      toast.error("Failed to delete budget")
    }
    setLoading(false)
  }

  const handleCancel = () => {
    setEditingId(null)
    setForm({ period_start: "", period_end: "", amount: "" })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY")
  }

  return (
    <div className="container-fluid px-4 py-4" style={{ maxWidth: "1200px" }}>
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold text-primary mb-3">Budget Management</h1>
        <p className="lead text-muted">Manage your hotel budgets and track spending periods</p>
      </div>

      {/* Budget Form */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0 d-flex align-items-center">
            <FaPlus className="me-2" />
            {editingId ? "Edit Budget" : "Create New Budget"}
          </h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            {editingId ? "Update the budget details below" : "Add a new budget period for your hotel"}
          </p>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaCalendarAlt className="me-2 text-primary" />
                    Period Start
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="period_start"
                    value={form.period_start}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaCalendarAlt className="me-2 text-primary" />
                    Period End
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="period_end"
                    value={form.period_end}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-4">
              <Form.Label className="d-flex align-items-center fw-semibold">
                <FaDollarSign className="me-2 text-success" />
                Budget Amount (₹)
              </Form.Label>
              <Form.Control
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
                className="form-control-lg"
              />
              <Form.Text className="text-muted">Enter amount in Indian Rupees (INR)</Form.Text>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button type="submit" disabled={loading} variant="primary" size="lg" className="flex-fill">
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : editingId ? (
                  "Update Budget"
                ) : (
                  "Create Budget"
                )}
              </Button>
              {editingId && (
                <Button type="button" variant="outline-secondary" onClick={handleCancel} size="lg">
                  <FaTimes className="me-2" />
                  Cancel
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Budget List */}
      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Budget Overview</h5>
          <small className="text-muted">All budget periods for this hotel (amounts in ₹)</small>
        </Card.Header>
        <Card.Body>
          {loading && budgets.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Loading budgets...</p>
            </div>
          ) : budgets.length === 0 ? (
            <div className="text-center py-5">
              <FaDollarSign size={48} className="text-muted mb-3" />
              <h4 className="text-muted">No budgets found</h4>
              <p className="text-muted">Create your first budget to get started</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Period</th>
                    <th>Amount (₹)</th>
                    <th>Duration</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-success">
                  {budgets.map((budget) => (
                    <tr key={budget.id} className="align-middle">
                      <td>
                        <Badge bg="dark" className="fs-6 p-2">
                          {formatDate(budget.period_start)} - {formatDate(budget.period_end)}
                        </Badge>
                      </td>
                      <td>
                        <span className="h4 text-success fw-bold">{formatCurrency(budget.amount)}</span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {moment(budget.period_end).diff(moment(budget.period_start), "days")} days
                        </small>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(budget)}
                            disabled={loading}
                            className="d-flex align-items-center"
                          >
                            <FaEdit className="me-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(budget.id)}
                            disabled={loading}
                            className="d-flex align-items-center"
                          >
                            <FaTrash className="me-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default BudgetManager
